import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { useAppSelector } from "../../state/store";
import { Row } from "../../components/Row";
import { shuffle } from "../../util/generalUtil";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { StringInput } from "../../components/Inputs";

interface Player {
  name: string;
  lives: number;
  turns: number;
}
interface Verse {
  book: number;
  chapter: number;
  verse: number;
  verseStr: string;
  words: string[];
}

export function VerseDetective() {
  useDocumentTitle("Verse Detective - Bible Quiz Academy");
  const location = useLocation();
  const material = useAppSelector((state) => state.material.material);
  const settings = useAppSelector((state) => state.settings);
  const { selectedChapters: chapters } = settings;
  const [players, setPlayers] = useState<Player[]>(
    shuffle(
      (location.state?.verseDetective?.players || []).map(
        (player: { name: string }) => ({
          name: player.name,
          lives: 3,
          turns: 0,
        })
      )
    )
  );
  const getRandomized = () => {
    // Generate the deck

    if (material.length === 0) return [];
    const verseResult: Verse[] = [];
    const isAll = location.state.verseDetective?.settings?.mode === "ALL";
    material.forEach((book, index) => {
      book.chapters.forEach((chapter, chapterIndex) => {
        if (
          (chapters && chapters[index] && chapters[index][chapterIndex]) ||
          isAll
        ) {
          const chapterFromSettings = chapters[index][chapterIndex];
          const chapterSettings =
            typeof chapterFromSettings === "boolean" || isAll
              ? Array(chapter.length).fill(chapterFromSettings || isAll)
              : chapterFromSettings;
          chapter.forEach((verse, verseIndex) => {
            if (chapterSettings[verseIndex] && verse.verse !== "No Verse") {
              const verseStr =
                material[index].chapters[chapterIndex][verseIndex].verse;
              verseResult.push({
                book: index,
                chapter: chapterIndex,
                verse: verseIndex,
                verseStr,
                words: verseStr
                  .replace(/[^a-zA-Z0-9\s]/g, "")
                  .toLocaleLowerCase()
                  .split(" "),
              });
            }
          });
        }
      });
    });
    return verseResult;
  };
  const [turn, setTurn] = useState(-1);
  const goToNextTurn = (playerTurn = turn, playerData = players) => {
    for (let i = 1; i < playerData.length; i++) {
      const thisIndex = (playerTurn + i) % playerData.length;
      if (playerData[thisIndex].lives > 0) {
        setTurn(thisIndex);
        return thisIndex;
      }
    }
    return -1;
  };
  const [allVerses, setAllVerses] = useState<Verse[]>(getRandomized()); // potential verses
  const [verseRemoved, setVerseRemoved] = useState(false); // if verse was previously removed because of being the only possibility
  const removeVerse = (verseToRemove: Verse) => {
    setAllVerses(
      allVerses.filter(
        (v) =>
          !(
            v.book === verseToRemove.book &&
            v.chapter === verseToRemove.chapter &&
            v.verse === verseToRemove.verse
          )
      )
    );
  };
  const eligibleVerses = useRef<Verse[]>([]); // possible verses based on words given
  const [phase, setPhase] = useState<"READY" | "ANSWERING" | "FINISHED">(
    "READY"
  );
  const [currentVerse, setCurrentVerse] = useState(""); // what's been given so far
  useEffect(() => {
    const el = document.getElementById("next-word");
    if (el) el.focus();
  }, [phase, currentVerse]);
  const [nextWord, setNextWord] = useState(""); // what the player has typed
  const [wordIndex, setWordIndex] = useState(-1);
  const [middleMessage, setMiddleMessage] = useState("");
  const timerRef = useRef<any>(null);
  const timeAllowed = 20;
  const [secondsLeft, setSecondsLeft] = useState(timeAllowed);
  const startTimer = (playerTurn = turn, newPlayers = players) => {
    const newId = setInterval(() => {
      setSecondsLeft((sec) => {
        const newTime = sec - 1;
        if (newTime > 0) return newTime;
        // Time's up!
        setMiddleMessage("You ran out of time. Here is a possible verse:");
        reduceLife(playerTurn, newPlayers, true);
        clearInterval(timerRef.current);
        return 0;
      });
    }, 1000);
    timerRef.current = newId;
  };
  const reduceLife = (
    playerTurn = turn,
    playerList = players,
    isTime = false
  ) => {
    const hint =
      eligibleVerses.current[
        Math.floor(Math.random() * eligibleVerses.current.length)
      ];
    setCurrentVerse(
      `${hint.verseStr}\n${material[hint.book].bookName} ${hint.chapter + 1}:${
        hint.verse + 1
      }`
    );
    if (!isTime)
      setMiddleMessage("Not a valid word! Here is a possible verse:");
    const newPlayers = [...playerList];
    newPlayers[playerTurn] = {
      ...players[playerTurn],
      lives: players[playerTurn].lives - 1,
    };
    if (eligibleVerses.current.length === 1) {
      // remove the verse
      removeVerse(eligibleVerses.current[0]);
      setVerseRemoved(true);
    }

    // Rearrange turn order
    const losingPlayer = newPlayers[playerTurn];
    newPlayers.splice(playerTurn, 1);
    let maxTurns = 0;
    newPlayers.forEach((p) => {
      if (p.turns > maxTurns) maxTurns = p.turns;
    });
    const eliminated: Player[] = [];
    const fewerTurns: Player[] = [];
    const moreTurns: Player[] = [];
    newPlayers.forEach((p) => {
      if (p.lives === 0) {
        eliminated.push(p);
      } else if (p.turns <= maxTurns - 2) {
        fewerTurns.push(p);
      } else {
        moreTurns.push(p);
      }
    });
    shuffle(fewerTurns);
    shuffle(moreTurns);
    const resultPlayers = [
      ...fewerTurns,
      ...moreTurns,
      losingPlayer,
      ...eliminated,
    ];
    setPlayers(resultPlayers);
    setTurn(-1);

    // Possibly end the game
    const remaining = resultPlayers.filter((p) => p.lives > 0);
    setPhase(remaining.length === 1 ? "FINISHED" : "READY");
  };

  if (players.length === 0) return <Navigate to="/" />;

  return (
    <div className="page">
      <Row>
        <Link className="secondary-btn" to="/">
          Go Back
        </Link>
        <h1 style={{ marginLeft: 10 }}>Verse Detective</h1>
      </Row>
      <div style={{ marginTop: 10, marginBottom: 20 }}>
        <div style={{ display: "flex" }}>
          <div style={{ width: 200 }}>Name</div>
          <div style={{ width: 50, marginLeft: 10 }}>Lives</div>
          <div style={{ marginLeft: 10 }}>Turns Taken</div>
        </div>
        {players.map((player, index) => (
          <div
            key={player.name}
            style={{
              display: "flex",
              marginTop: 4,
              textDecoration: player.lives === 0 ? "line-through" : undefined,
            }}
          >
            <div
              style={{
                width: 200,
                backgroundColor:
                  turn === index
                    ? settings.lightMode
                      ? "lightblue"
                      : "darkblue"
                    : undefined,
              }}
            >
              {player.name}
            </div>
            <div style={{ width: 50, marginLeft: 10 }}>{player.lives}</div>
            <div style={{ marginLeft: 10 }}>{player.turns}</div>
          </div>
        ))}
      </div>
      {middleMessage && <p>{middleMessage}</p>}
      {currentVerse && (
        <p style={{ whiteSpace: "pre-line", fontSize: 18 }}>{currentVerse}</p>
      )}
      {verseRemoved && phase !== "FINISHED" && (
        <p>Because that was the only possible verse, it has been removed.</p>
      )}
      {phase !== "FINISHED" && (
        <div style={{ marginTop: 10 }}>
          {phase === "READY" && (
            <>
              <button
                className="clickable"
                key="next-verse"
                style={{ marginTop: 10 }}
                onClick={() => {
                  if (allVerses.length === 0) {
                    // Edge case for exhausting all verses
                    setPhase("FINISHED");
                    setMiddleMessage(
                      "All verses have been guessed! The game is over."
                    );
                    eligibleVerses.current = [];
                    return;
                  }
                  setPhase("ANSWERING");
                  setVerseRemoved(false);

                  // Pick a random verse from the available material
                  const verseIndex = Math.floor(
                    Math.random() * allVerses.length
                  );
                  const thisVerse = allVerses[verseIndex];
                  const firstWord = thisVerse.words[0];
                  setCurrentVerse(firstWord);
                  eligibleVerses.current = allVerses.filter(
                    (v) => v.words[0] === firstWord
                  );
                  setWordIndex(1);

                  setNextWord("");
                  setSecondsLeft(timeAllowed);
                  const newTurn = 0;
                  setTurn(newTurn);
                  startTimer(newTurn);
                  setMiddleMessage("");
                }}
              >
                New Verse
              </button>
            </>
          )}
          {phase === "ANSWERING" && (
            <>
              <StringInput
                caption="Next Word:"
                value={nextWord}
                onChange={setNextWord}
                spacing={false}
                id="next-word"
              />
              <br />
              <button
                className="clickable"
                onClick={() => {
                  if (!nextWord.trim()) return;
                  clearInterval(timerRef.current);
                  const guessedWord = nextWord
                    .toLocaleLowerCase()
                    .replace(/[^a-z0-9\s]/g, "");
                  const newContent = currentVerse + " " + guessedWord;

                  const newPlayers = [...players];
                  newPlayers[turn].turns += 1;
                  // Check for eligibility
                  let newEligible = eligibleVerses.current.filter(
                    (v) => v.words[wordIndex] === guessedWord
                  );
                  if (newEligible.length === 0) {
                    // Missed word
                    reduceLife(turn, newPlayers);
                    return;
                  }
                  setPlayers(newPlayers);

                  // Check for a finished verse
                  const finishedVerse = newEligible.findIndex(
                    (v) => v.words.length === wordIndex + 1
                  );
                  if (finishedVerse >= 0) {
                    // End of verse
                    const finishedObj = newEligible[finishedVerse];
                    newEligible = [finishedObj];
                    setCurrentVerse(
                      `${finishedObj.verseStr}\n${
                        material[finishedObj.book].bookName
                      } ${finishedObj.chapter + 1}:${finishedObj.verse + 1}`
                    );
                    setPhase("READY");
                    removeVerse(finishedObj);
                    setMiddleMessage(
                      "Verse finished! You cannot use this verse any more."
                    );
                  } else {
                    // Continuation of verse
                    setCurrentVerse(newContent);
                    const newTurn = goToNextTurn();
                    setWordIndex(wordIndex + 1);
                    setSecondsLeft(timeAllowed);
                    startTimer(newTurn);
                  }
                  eligibleVerses.current = newEligible;
                  setNextWord("");
                }}
              >
                Submit Word
              </button>
              <p>{`${eligibleVerses.current.length} Possible Verse${
                eligibleVerses.current.length === 1 ? "" : "s"
              }`}</p>
              <p>{`${secondsLeft} Seconds`}</p>
            </>
          )}
        </div>
      )}
      {phase === "FINISHED" && (
        <p style={{ fontSize: 18, marginTop: 40 }}>Game Over!</p>
      )}
    </div>
  );
}
